class HeroPlpSlider extends HTMLElement {
  /** @type {HTMLElement[]} */
  #slides = [];

  /** @type {HTMLButtonElement[]} */
  #dots = [];

  /** @type {number} */
  #currentIndex = 0;

  /** @type {number | null} */
  #autoplayTimer = null;

  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;

  /** @type {boolean} */
  #isVisible = true;

  /** @type {boolean} */
  #isHovering = false;

  #onVisibilityChange = () => {
    this.#isVisible = !document.hidden;
    if (this.#isVisible) {
      this.#startAutoplay();
      return;
    }
    this.#stopAutoplay();
  };

  connectedCallback() {
    this.#slides = Array.from(this.querySelectorAll('[data-slide]'));
    this.#dots = Array.from(this.querySelectorAll('[data-dot]'));

    if (!this.#slides.length) return;

    this.#bindControls();
    this.#bindDots();
    this.#bindHoverPause();
    this.#observeViewport();

    document.addEventListener('visibilitychange', this.#onVisibilityChange);

    this.#goTo(0);
    this.#startAutoplay();
  }

  disconnectedCallback() {
    this.#stopAutoplay();
    document.removeEventListener('visibilitychange', this.#onVisibilityChange);

    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
      this.#intersectionObserver = null;
    }
  }

  #bindControls() {
    const prevButton = this.querySelector('[data-prev]');
    const nextButton = this.querySelector('[data-next]');

    if (prevButton) {
      prevButton.addEventListener('click', () => this.#previous());
    }

    if (nextButton) {
      nextButton.addEventListener('click', () => this.#next());
    }
  }

  #bindDots() {
    for (const dot of this.#dots) {
      dot.addEventListener('click', () => {
        const index = Number(dot.getAttribute('data-index'));
        if (Number.isNaN(index)) return;
        this.#goTo(index);
      });
    }
  }

  #bindHoverPause() {
    if (!this.#shouldPauseOnHover()) return;

    this.addEventListener('mouseenter', () => {
      this.#isHovering = true;
      this.#stopAutoplay();
    });

    this.addEventListener('mouseleave', () => {
      this.#isHovering = false;
      this.#startAutoplay();
    });
  }

  #observeViewport() {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          this.#isVisible = entry.isIntersecting;
          if (this.#isVisible) {
            this.#startAutoplay();
            return;
          }
          this.#stopAutoplay();
        }
      },
      {
        rootMargin: '100px',
      }
    );

    this.#intersectionObserver = observer;
    observer.observe(this);
  }

  /**
   * @param {number} nextIndex
   */
  #goTo(nextIndex) {
    const maxIndex = this.#slides.length - 1;
    let safeIndex = nextIndex;

    if (this.#loopEnabled()) {
      if (safeIndex > maxIndex) safeIndex = 0;
      if (safeIndex < 0) safeIndex = maxIndex;
    } else {
      if (safeIndex > maxIndex) safeIndex = maxIndex;
      if (safeIndex < 0) safeIndex = 0;
    }

    this.#currentIndex = safeIndex;

    this.#slides.forEach((slide, index) => {
      const isActive = index === this.#currentIndex;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', String(!isActive));
    });

    // Sync full-width background media outside the section container
    const fullbg = this.closest('.hero-plp__fullbg');
    if (fullbg) {
      fullbg.querySelectorAll('[data-slide-media]').forEach((media) => {
        const mediaIndex = Number(media.getAttribute('data-slide-media'));
        media.classList.toggle('is-active', mediaIndex === this.#currentIndex);
      });
    }

    this.#dots.forEach((dot, index) => {
      const isActive = index === this.#currentIndex;
      dot.classList.toggle('is-active', isActive);
      if (isActive) {
        dot.setAttribute('aria-current', 'true');
        return;
      }
      dot.removeAttribute('aria-current');
    });
  }

  #next() {
    this.#goTo(this.#currentIndex + 1);
  }

  #previous() {
    this.#goTo(this.#currentIndex - 1);
  }

  #startAutoplay() {
    if (!this.#shouldAutoplay()) return;
    if (this.#autoplayTimer !== null) return;

    const speedInSeconds = Number(this.dataset.speed || '5');
    const speedInMs = Math.max(2000, speedInSeconds * 1000);

    this.#autoplayTimer = setInterval(() => {
      this.#next();
    }, speedInMs);
  }

  #stopAutoplay() {
    if (this.#autoplayTimer === null) return;
    clearInterval(this.#autoplayTimer);
    this.#autoplayTimer = null;
  }

  #shouldAutoplay() {
    if (this.#slides.length < 2) return false;
    if (this.dataset.autoplay !== 'true') return false;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
    if (!this.#isVisible) return false;
    if (this.#isHovering) return false;
    return true;
  }

  #loopEnabled() {
    return this.dataset.loop === 'true';
  }

  #shouldPauseOnHover() {
    if (this.dataset.pauseHover !== 'true') return false;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return false;
    return true;
  }
}

class HeroPlpCountdown extends HTMLElement {
  /** @type {number | null} */ #intervalId = null;

  static get observedAttributes() {
    return ['data-target'];
  }

  connectedCallback() {
    this.#start();
  }

  disconnectedCallback() {
    this.#stop();
  }

  attributeChangedCallback() {
    this.#stop();
    this.#start();
  }

  #start() {
    this.#tick();
    this.#intervalId = setInterval(() => this.#tick(), 1000);
  }

  #stop() {
    if (this.#intervalId !== null) {
      clearInterval(this.#intervalId);
      this.#intervalId = null;
    }
  }

  #tick() {
    const target = this.dataset.target;
    if (!target) return;

    const dateStr = target.includes('T') ? target : `${target}T00:00:00`;
    const elapsed = Math.max(0, Date.now() - new Date(dateStr).getTime());
    const total = Math.floor(elapsed / 1000);

    const dEl = this.querySelector('[data-d]');
    const hEl = this.querySelector('[data-h]');
    const mEl = this.querySelector('[data-m]');
    const sEl = this.querySelector('[data-s]');

    if (dEl) dEl.textContent = String(Math.floor(total / 86400));
    if (hEl) hEl.textContent = String(Math.floor((total % 86400) / 3600));
    if (mEl) mEl.textContent = String(Math.floor((total % 3600) / 60));
    if (sEl) sEl.textContent = String(total % 60);
  }
}

if (!customElements.get('hero-countdown')) {
  customElements.define('hero-countdown', HeroPlpCountdown);
}

if (!customElements.get('hero-plp-slider')) {
  customElements.define('hero-plp-slider', HeroPlpSlider);
}
